/**
 * OpenAI Assistants API Service
 * 
 * Service pour interagir avec l'Assistant PRIsME configuré
 * (Créer threads, envoyer messages, recevoir réponses)
 */

import { refreshToken } from './authService';

// Base des routes API côté backend pour l'assistant
const API_BASE_URL = '/api/prisme';

async function fetchAssistantApi(input: RequestInfo | URL, init: RequestInit): Promise<Response> {
  const response = await fetch(input, { ...init, credentials: 'include' });
  if (response.status !== 401) {
    return response;
  }

  // Le accessToken expire avant le refreshToken. LEX doit tenter le refresh
  // avant d'afficher « Impossible de créer la session ».
  const refreshed = await refreshToken();
  if (!refreshed) {
    return response;
  }

  return fetch(input, { ...init, credentials: 'include' });
}

interface AssistantIds {
  assistantId: string | null;
  vectorStoreId?: string | null;
}

/**
 * Récupérer les IDs Assistant/VectorStore
 * - Toujours depuis le backend (config globale) pour que tout le monde ait la même version après une synchro
 * - Fallback localStorage seulement si le serveur ne répond pas (hors ligne / pas encore configuré)
 */
async function getAssistantIdsFromConfig(): Promise<AssistantIds> {
  // 1. Source de vérité = serveur (peu importe qui a synchronisé, tout le monde utilise le même assistant)
  try {
    const response = await fetch(`${API_BASE_URL}/assistant/config`, { credentials: 'include' });

    if (response.ok) {
      const data = await response.json();
      const assistantId = data.assistant_id || data.assistantId || null;
      const vectorStoreId = data.vector_store_id || data.vectorStoreId || null;

      if (assistantId && typeof window !== 'undefined') {
        localStorage.setItem('prisme_assistant_id', assistantId);
      }
      if (vectorStoreId && typeof window !== 'undefined') {
        localStorage.setItem('prisme_vector_store_id', vectorStoreId);
      }
      return { assistantId, vectorStoreId };
    }
  } catch {
    // Réseau ou serveur indisponible: fallback sur le cache local
  }

  // 2. Fallback: cache local (hors ligne ou pas encore configuré)
  const localAssistantId = typeof window !== 'undefined'
    ? localStorage.getItem('prisme_assistant_id')
    : null;
  const localVectorStoreId = typeof window !== 'undefined'
    ? localStorage.getItem('prisme_vector_store_id')
    : null;

  return { assistantId: localAssistantId, vectorStoreId: localVectorStoreId };
}

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  sources?: string[]; // Citations des fichiers PRIsME
}

export interface SourceDetail {
  fileId: string;
  displayName: string;
  fileName?: string | null;
  citationTexts: string[];
  indices: Array<{
    start: number;
    end: number;
    text: string;
  }>;
}

export interface AssistantResponse {
  message: string;
  sources: string[] | SourceDetail[]; // Support ancien format (string[]) et nouveau (SourceDetail[])
  success: boolean;
}

/**
 * Créer un nouveau thread de conversation
 */
export async function createThread(): Promise<string> {
  const response = await fetchAssistantApi(`${API_BASE_URL}/assistant/thread`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(error?.error || 'Erreur lors de la création du thread Assistant');
  }

  const data = await response.json();
  return data.threadId;
}

/**
 * Envoyer un message à l'Assistant et obtenir la réponse
 */
export async function sendMessageToAssistant(
  threadId: string,
  userMessage: string
): Promise<AssistantResponse> {
  const { assistantId } = await getAssistantIdsFromConfig();
  
  if (!assistantId) {
    throw new Error('Assistant non configuré. Exécute le setup d\'abord.');
  }

  try {
    const response = await fetchAssistantApi(`${API_BASE_URL}/assistant/message`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        threadId,
        userMessage,
        assistantId,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(error?.error || 'Erreur lors de l\'appel à l\'Assistant PRIsME');
    }

    const data = await response.json();

    return {
      message: data.message,
      sources: data.sources || [],
      success: data.success ?? true,
    };
  } catch (error) {
    console.error('Error sending message:', error);
    return {
      message: `❌ Erreur: ${error instanceof Error ? error.message : 'Erreur inconnue'}`,
      sources: [],
      success: false
    };
  }
}

/**
 * Récupérer l'historique d'un thread
 */
export async function getThreadMessages(_threadId: string): Promise<Message[]> {
  console.warn('getThreadMessages non implémenté côté backend pour le moment');
  return [];
}

/**
 * Supprimer un thread (cleanup)
 */
export async function deleteThread(_threadId: string): Promise<boolean> {
  console.warn('deleteThread non implémenté côté backend pour le moment');
  return true;
}

/**
 * Helper: Vérifier si le service est disponible
 */
export function isAssistantServiceAvailable(): boolean {
  // On ne peut pas faire d'appel async ici, donc on vérifie simplement le cache local.
  if (typeof window === 'undefined') {
    return false;
  }
  const assistantId = localStorage.getItem('prisme_assistant_id');
  return !!assistantId;
}

